/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * React Hook that calculates scroll percentage.
 *
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.12.0
 */

import { useCallback, useEffect, useState } from 'react';

export default function useScrollPercentage(scrollCallBack: (e: Event, position: number) => void) {
    const [scrollPercentage, setScrollPercentage] = useState(NaN);

    const reportScroll = useCallback(e => {
        setScrollPercentage(getScrollPercentage());
        scrollCallBack(e, scrollPercentage);
    }, [scrollCallBack, scrollPercentage]);

    useEffect(
        () => {
            window.addEventListener('scroll', reportScroll, { passive: true });
            if (Number.isNaN(scrollPercentage)) {
                setScrollPercentage(getScrollPercentage());
            }
            return () => {
                window.removeEventListener('scroll', reportScroll);
            };
        },
        [reportScroll, scrollPercentage],
    );

    return [Number.isNaN(scrollPercentage) ? 0 : scrollPercentage];
}

function getScrollPercentage() {
    return Math.round(window.scrollY / (document.body.scrollHeight - document.body.clientHeight) * 100);
}
